import { makeRequest } from './api';
import { REMOTE_FLOW_LABEL } from '../config/remoteFlowHost';
import {
  clampFlowRowLimit,
  FLOW_OPPORTUNITIES_DEFAULT_LIMIT,
  FLOW_OPPORTUNITIES_MAX_LIMIT,
} from '../config/flowApiLimits';

// Core Data Interfaces
export interface UnderlyingPrice {
  ticker: string;
  price: number;
  change: number;
  change_percent: number;
  volume: number;
  last_updated: string;
}

export interface OptionContract {
  id: number;
  ticker: string;
  contract_ticker: string;
  strike_price: number;
  expiry_date: string;
  option_type: 'call' | 'put';
  volume: number;
  open_interest: number;
  last_price: number;
  bid: number;
  ask: number;
  underlying_price: number;
  moneyness: 'ITM' | 'ATM' | 'OTM';
  gamma: number;
  delta: number;
  theta: number;
  vega: number;
  iv: number;
  created_at: string;
  updated_at: string;
}

export interface FlowOpportunity {
  id: number;
  contract_ticker: string;
  symbol: string;
  strike_price: number;
  expiry_date: string;
  option_type: 'call' | 'put';
  volume: number;
  open_interest: number;
  last_price: number;
  underlying_price: number;
  moneyness: string;
  flow_score: number;
  premium_size: number;
  unusual_activity_score: number;
  trade_count: number;
  total_trade_size: number;
  avg_trade_price?: number;
  created_at: string;
  /** Preset Smart Flow nomination fields (backend strategy rules) */
  strategy_id?: string;
  strategy_name?: string;
  conviction?: 'GOLDEN' | 'SILVER' | 'BRONZE' | 'MONITOR' | string;
  win_rate_pct?: number | null;
  nomination_score?: number;
  nomination_id?: string;
  otm_pct?: number;
  aggression_pct?: number;
  trade_style?: string;
  symbol_tier?: string;
  triggers_matched?: string[];
  chain_put_call_oi?: number;
  vol_anomaly?: number;
  // Display fields
  time?: string;
  label?: string;
  logo?: string;
  premium?: number;
  prems_display?: string;
  vol_display?: string;
  oi_display?: string;
  exp_display?: string;
  spot_price?: number;
  call_put?: string;
  isNew?: boolean;
  ageMinutes?: number;
  /** Tape classification: Bid / Ask / Mid */
  side_bid_ask?: string;
  /** buy / sell / mid for display */
  execution_side?: string;
  /** OPRA / Theta trade metadata (from tape or historical APIs) */
  condition?: number;
  sequence?: number;
  exchange?: number;
  ext_condition1?: number;
  ext_condition2?: number;
  ext_condition3?: number;
  ext_condition4?: number;
  /** Optional first-order theta on enriched tape rows */
  theta?: number;
  delta?: number;
  gamma?: number;
  vega?: number;
  implied_volatility?: number;
  iv_rank?: number;
  dte?: number;
  asset_class?: string;
  cap_tier?: string;
  bid?: number;
  ask?: number;
  theta_payload_json?: string;
  theta_payload?: Record<string, unknown>;
}

export interface OptionsStatistics {
  symbol: string;
  underlying_price: number;
  total_contracts: number;
  total_volume: number;
  total_open_interest: number;
  calls_count: number;
  puts_count: number;
  itm_count: number;
  atm_count: number;
  otm_count: number;
  avg_flow_score: number;
  top_opportunities: FlowOpportunity[];
  gamma_exposure: {
    total_gamma: number;
    call_gamma: number;
    put_gamma: number;
    net_gamma: number;
  };
  volume_analysis: {
    call_volume: number;
    put_volume: number;
    put_call_ratio: number;
    volume_skew: number;
  };
  last_updated: string;
}

export interface BidAskVolume {
  contract_ticker: string;
  symbol: string;
  strike_price: number;
  expiry_date: string;
  /** API may omit or use alternate keys — UI must guard */
  option_type?: 'call' | 'put' | string;
  bid_volume: number;
  ask_volume: number;
  bid_ask_ratio?: number;
  volume_imbalance: number;
  underlying_price: number;
  last_updated: string;
}

export interface WhalesPositioning {
  contract_ticker: string;
  symbol: string;
  strike_price: number;
  expiry_date: string;
  option_type: 'call' | 'put';
  large_trades: number;
  total_premium: number;
  avg_trade_size: number;
  max_trade_size: number;
  positioning_score: number;
  underlying_price: number;
  last_updated: string;
}

export interface FlowAnalysisResult {
  success: boolean;
  analysis?: {
    contract_ticker: string;
    underlying: string;
    strike: number;
    type: string;
    volume: number;
    underlying_price: number;
    analysis: string;
    recommendation: string;
    confidence: number;
    trades_found: number;
    timestamp: string;
    total_premium: number;
    total_trades: number;
    // Enhanced analysis data
    buy_sell_analysis?: {
      direction: 'BUY' | 'SELL' | 'NEUTRAL';
      confidence: number;
      indicators: {
        oi_change: number;
        premium_size: number;
        volume: number;
        spread: number;
        moneyness: string;
      };
    };
    entry_exit_analysis?: {
      position: 'ENTRY' | 'EXIT' | 'UNCLEAR';
      confidence: number;
      indicators: {
        oi_change: number;
        premium_per_contract: number;
        volume_concentration: number;
        iv_level: number;
      };
    };
    market_metrics?: {
      current_oi: number;
      current_iv: number;
      current_bid: number;
      current_ask: number;
      current_spread: number;
      estimated_oi_change: number;
    };
    volume_analysis?: {
      total_volume?: number;
      average_volume?: number;
    };
    session_contract_volume?: number;
    effective_volume_for_scoring?: number;
    quote_only_flow?: boolean;
  };
  error?: string;
  timestamp: string;
}

export interface SmartFlowStatusData {
  success?: boolean;
  error?: string;
  is_running?: boolean;
  current_phase?: 'idle' | 'collecting_chains' | 'analyzing_priority' | 'updating';
  progress_percent?: number;
  total_tickers?: number;
  processed_tickers?: number;
  total_contracts?: number;
  processed_contracts?: number;
  opportunities_found?: number;
  last_updated?: string;
  estimated_completion?: string;
}

// Smart Flow Service
export const smartFlowService = {
  // Status and Control
  getStatus: async (): Promise<SmartFlowStatusData> => {
    try {
      const response = await makeRequest('/api/smart-flow/status', 'GET');
      return response;
    } catch (error: any) {
      console.error('Error fetching smart flow status:', error);
      return {
        is_running: false,
        current_phase: 'idle',
        progress_percent: 0,
        total_tickers: 0,
        processed_tickers: 0,
        total_contracts: 0,
        processed_contracts: 0,
        opportunities_found: 0,
        last_updated: new Date().toISOString(),
        estimated_completion: ''
      };
    }
  },

  startCollection: async (): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await makeRequest('/api/smart-flow/collect-chains', 'POST');
      return response;
    } catch (error: any) {
      console.error('Error starting collection:', error);
      return { success: false, message: error.message };
    }
  },

  startAnalysis: async (): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await makeRequest('/api/smart-flow/analyze-priority', 'POST');
      return response;
    } catch (error: any) {
      console.error('Error starting analysis:', error);
      return { success: false, message: error.message };
    }
  },

  // Data Access (All cached, no direct API calls)
  getUnderlyingPrice: async (ticker: string): Promise<UnderlyingPrice> => {
    try {
      const response = await makeRequest(`/api/smart-flow/underlying-price/${ticker}`, 'GET');
      return response;
    } catch (error: any) {
      console.error(`Error fetching underlying price for ${ticker}:`, error);
      throw error;
    }
  },

  getOptionsChain: async (ticker: string): Promise<OptionContract[]> => {
    try {
      const response = await makeRequest(`/api/smart-flow/options-chain/${ticker}`, 'GET');
      return response.data || [];
    } catch (error: any) {
      console.error(`Error fetching options chain for ${ticker}:`, error);
      return [];
    }
  },

  getFlowOpportunities: async (params?: {
    symbol?: string;
    underlyings?: string;
    mode?: 'auto' | 'live' | 'historical';
    limit?: number;
    min_flow_score?: number;
    min_oi?: number;
    min_volume?: number;
    min_premium?: number;
    option_type?: 'call' | 'put' | 'all';
    moneyness?: 'ITM' | 'ATM' | 'OTM' | 'all';
    /** Extra GET query keys (e.g. asset_class, cap_tier, price_bucket) for server-side reference filters. */
    referenceQuery?: Record<string, string>;
    /** MMEC preset strategy id or 'all' */
    strategy?: string;
    conviction?: 'GOLDEN' | 'SILVER' | 'BRONZE' | 'MONITOR' | 'all';
    /** 1 = preset nominations (default); 0 = raw tape */
    preset?: 0 | 1;
    /** Server-side pagination for Smart Flow fallback tape/noms. */
    offset?: number;
    /** Override default client timeout (ms). */
    requestTimeoutMs?: number;
  }): Promise<{
    data: FlowOpportunity[];
    total: number;
    runtime_mode?: 'live' | 'historical';
    backfill_updated_at?: number;
    serviceAvailable?: boolean;
    errorMessage?: string;
    preset_mode?: boolean;
    nominations?: unknown[];
    strategies?: string[];
    raw_prints_scanned?: number;
    has_more?: boolean;
    offset?: number;
  }> => {
    try {
      const { referenceQuery, limit: reqLimit, offset: reqOffset, ...rest } = params || {};
      const merged: Record<string, unknown> = {
        min_oi: 0,
        min_volume: 0,
        min_premium: 0,
        preset: 1,
        ...rest,
        limit: clampFlowRowLimit(
          reqLimit,
          FLOW_OPPORTUNITIES_DEFAULT_LIMIT,
          FLOW_OPPORTUNITIES_MAX_LIMIT,
        ),
      };
      if (typeof reqOffset === 'number' && reqOffset > 0) {
        merged.offset = reqOffset;
      }
      if (referenceQuery) {
        Object.entries(referenceQuery).forEach(([k, v]) => {
          if (v != null && v !== '') merged[k] = v;
        });
      }
      const response = await makeRequest<any>(
        '/api/smart-flow/opportunities',
        'GET',
        merged,
        undefined,
        { timeoutMs: rest.requestTimeoutMs ?? 120000 }
      );
      return {
        data: response.data || [],
        total: response.total ?? 0,
        runtime_mode: response.runtime_mode,
        backfill_updated_at: response.backfill_updated_at,
        serviceAvailable: response.success !== false,
        preset_mode: response.preset_mode === true,
        nominations: response.nominations,
        strategies: response.strategies,
        raw_prints_scanned: response.raw_prints_scanned,
        has_more: response.has_more === true,
        offset: typeof response.offset === 'number' ? response.offset : undefined,
      };
    } catch (error: any) {
      const msg = error?.message || 'Request failed';
      const gatewayTimeout =
        'Request timed out or server busy. Try again or use fewer filters.';
      const isTimeout =
        error?.name === 'AbortError' ||
        msg === gatewayTimeout ||
        /timed out|timeout/i.test(msg);
      console.warn('Error fetching flow opportunities:', msg);
      return {
        data: [],
        total: 0,
        serviceAvailable: false,
        errorMessage: isTimeout
          ? `Smart Flow scan timed out on ${REMOTE_FLOW_LABEL} (full preset scan ~20–60s). Enter a symbol in the stable bar, narrow Strategy/Conviction, wait, and Refresh.`
          : msg,
      };
    }
  },

  getOptionsStatistics: async (ticker: string): Promise<OptionsStatistics> => {
    try {
      const response = await makeRequest<any>(`/api/smart-flow/options-statistics/${ticker}`, 'GET');
      const raw =
        response && typeof response === 'object' && response.data && typeof response.data === 'object'
          ? response.data
          : response;
      if (!raw || typeof raw !== 'object') {
        throw new Error('Invalid options statistics response');
      }
      const zeroGamma = { total_gamma: 0, call_gamma: 0, put_gamma: 0, net_gamma: 0 };
      const zeroVol = { call_volume: 0, put_volume: 0, put_call_ratio: 0, volume_skew: 0 };
      const ge = raw.gamma_exposure && typeof raw.gamma_exposure === 'object' ? raw.gamma_exposure : {};
      const va = raw.volume_analysis && typeof raw.volume_analysis === 'object' ? raw.volume_analysis : {};
      return {
        symbol: String(raw.symbol ?? ticker).toUpperCase(),
        underlying_price: Number(raw.underlying_price ?? 0) || 0,
        total_contracts: Number(raw.total_contracts ?? 0) || 0,
        total_volume: Number(raw.total_volume ?? 0) || 0,
        total_open_interest: Number(raw.total_open_interest ?? 0) || 0,
        calls_count: Number(raw.calls_count ?? 0) || 0,
        puts_count: Number(raw.puts_count ?? 0) || 0,
        itm_count: Number(raw.itm_count ?? 0) || 0,
        atm_count: Number(raw.atm_count ?? 0) || 0,
        otm_count: Number(raw.otm_count ?? 0) || 0,
        avg_flow_score: Number(raw.avg_flow_score ?? 0) || 0,
        top_opportunities: Array.isArray(raw.top_opportunities) ? raw.top_opportunities : [],
        gamma_exposure: {
          total_gamma: Number(ge.total_gamma ?? 0) || 0,
          call_gamma: Number(ge.call_gamma ?? 0) || 0,
          put_gamma: Number(ge.put_gamma ?? 0) || 0,
          net_gamma: Number(ge.net_gamma ?? 0) || 0,
        },
        volume_analysis: {
          call_volume: Number(va.call_volume ?? 0) || 0,
          put_volume: Number(va.put_volume ?? 0) || 0,
          put_call_ratio: Number(va.put_call_ratio ?? 0) || 0,
          volume_skew: Number(va.volume_skew ?? 0) || 0,
        },
        last_updated: String(raw.last_updated || new Date().toISOString()),
      };
    } catch (error: any) {
      console.error(`Error fetching options statistics for ${ticker}:`, error);
      throw error;
    }
  },

  getBidAskVolume: async (ticker: string): Promise<BidAskVolume[]> => {
    const sym = encodeURIComponent((ticker || '').trim());
    const response = await makeRequest<any>(`/api/smart-flow/bid-ask-volume/${sym}`, 'GET');
    if (response && response.success === false && response.error) {
      throw new Error(String(response.error));
    }
    const raw = response?.data ?? response?.results;
    if (!Array.isArray(raw)) return [];
    const pick = (row: Record<string, unknown>, keys: string[]): unknown => {
      for (const k of keys) {
        if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k];
      }
      return undefined;
    };
    const num = (v: unknown): number => {
      if (v === undefined || v === null || v === '') return 0;
      if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
      const n = parseFloat(String(v).replace(/,/g, '').trim());
      return Number.isFinite(n) ? n : 0;
    };
    const parseContract = (contractRaw: unknown): { root: string; optionType?: 'call' | 'put'; strike?: number } => {
      const c = String(contractRaw ?? '').trim().toUpperCase().replace(/^O:/, '');
      if (!c) return { root: '' };
      const m = c.match(/^([A-Z]{1,6})\d{6}([CP])(\d{8})$/);
      if (!m) return { root: c };
      const strike = parseInt(m[3], 10) / 1000;
      return {
        root: m[1],
        optionType: m[2] === 'P' ? 'put' : 'call',
        strike: Number.isFinite(strike) ? strike : undefined,
      };
    };
    return raw
      .map((row: Record<string, unknown>) => {
        const contract = String(pick(row, ['contract_ticker', 'contractTicker', 'Contract', 'Symbol']) ?? '');
        const parsed = parseContract(contract);
        const bid = num(
          pick(row, [
            'bid_volume', 'bidVolume', 'Bid_Volume', 'Bid Volume', 'BidVolume',
            'bid_size', 'BidSize', 'bidSize',
          ]),
        );
        const ask = num(
          pick(row, [
            'ask_volume', 'askVolume', 'Ask_Volume', 'Ask Volume', 'AskVolume',
            'ask_size', 'AskSize', 'askSize',
          ]),
        );
        const optionRaw = String(
          pick(row, ['option_type', 'optionType', 'Option Type', 'option', 'type']) ?? '',
        ).trim().toLowerCase();
        const optionType =
          optionRaw === 'put' || optionRaw === 'p'
            ? 'put'
            : optionRaw === 'call' || optionRaw === 'c'
              ? 'call'
              : parsed.optionType ?? 'call';
        const strike = num(pick(row, ['strike_price', 'strike', 'Strike', 'Strike Price'])) || (parsed.strike ?? 0);
        const imbalance = num(
          pick(row, ['volume_imbalance', 'volumeImbalance', 'Volume Imbalance', 'imbalance']),
        );
        return {
          contract_ticker: contract,
          symbol: String(pick(row, ['symbol', 'Symbol', 'Underlying', 'Ticker']) ?? parsed.root ?? ticker).trim(),
          strike_price: strike,
          expiry_date: String(pick(row, ['expiry_date', 'expiry', 'Expiry Date']) ?? ''),
          option_type: optionType,
          bid_volume: bid,
          ask_volume: ask,
          bid_ask_ratio: pick(row, ['bid_ask_ratio', 'bidAskRatio', 'Bid Ask Ratio']) != null
            ? num(pick(row, ['bid_ask_ratio', 'bidAskRatio', 'Bid Ask Ratio']))
            : ask > 0
              ? bid / ask
              : bid > 0
                ? 999
                : 0,
          volume_imbalance: imbalance !== 0 ? imbalance : bid - ask,
          underlying_price: num(pick(row, ['underlying_price', 'underlyingPrice', 'Underlying Price', 'Spot', 'Stock'])),
          last_updated: String(pick(row, ['last_updated', 'lastUpdated', 'updated_at']) ?? new Date().toISOString()),
        } as BidAskVolume;
      })
      .filter((r) => r.contract_ticker.length > 0);
  },

  getGammaExposure: async (ticker: string): Promise<{
    total_gamma: number;
    call_gamma: number;
    put_gamma: number;
    net_gamma: number;
    contracts: Array<{
      contract_ticker: string;
      strike_price: number;
      gamma: number;
      open_interest: number;
      gamma_exposure: number;
    }>;
  }> => {
    try {
      const response = await makeRequest(`/api/smart-flow/gamma-exposure/${ticker}`, 'GET');
      return response;
    } catch (error: any) {
      console.error(`Error fetching gamma exposure for ${ticker}:`, error);
      throw error;
    }
  },

  getWhalesPositioning: async (ticker: string): Promise<WhalesPositioning[]> => {
    try {
      const response = await makeRequest(`/api/smart-flow/whales-positioning/${ticker}`, 'GET');
      return response.data || [];
    } catch (error: any) {
      console.error(`Error fetching whales positioning for ${ticker}:`, error);
      return [];
    }
  },

  analyzeFlowOpportunity: async (params: {
    underlying: string;
    expiry: string;
    strike: number;
    side: string;
    volume: number;
    date: string;
    premium: number;
  }): Promise<FlowAnalysisResult> => {
    try {
      const response = await makeRequest('/api/smart-flow/analyze-opportunity', 'POST', {}, params);
      return response;
    } catch (error: any) {
      console.error('Error analyzing flow opportunity:', error);
      return { success: false, error: error.message, timestamp: new Date().toISOString() };
    }
  },

  getAvailableSymbols: async (): Promise<string[]> => {
    try {
      const response = await makeRequest<any>('/api/smart-flow/available-symbols', 'GET');
      return response.data || [];
    } catch (error: any) {
      console.error('Error fetching available symbols:', error);
      return [];
    }
  },

  getAvailableExpiries: async (ticker: string): Promise<string[]> => {
    try {
      const response = await makeRequest(`/api/smart-flow/available-expiries/${ticker}`, 'GET');
      return response.data || [];
    } catch (error: any) {
      console.error(`Error fetching available expiries for ${ticker}:`, error);
      return [];
    }
  },

  getAvailableStrikes: async (ticker: string, expiry: string): Promise<number[]> => {
    try {
      const response = await makeRequest(`/api/smart-flow/available-strikes/${ticker}/${expiry}`, 'GET');
      return response.data || [];
    } catch (error: any) {
      console.error(`Error fetching available strikes for ${ticker}/${expiry}:`, error);
      return [];
    }
  }
};

export default smartFlowService;
